import type { SubscriptionStatusResponse } from "@/clients/models/SubscriptionStatusResponse";
import { SubscriptionsService } from "@/clients/services/SubscriptionsService";
import { guardGetUserFromLocalStorage } from "@/utils/request";

// Backend contract: on 502, retry with exponential backoff (max 3 attempts)
// before surfacing the error. See nutricoach-backend/docs/subscriptions_frontend_guide.md §4.
const RETRY_DELAYS_MS = [1000, 2000];

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const isBadGateway = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  return (error as { status?: number }).status === 502;
};

export const verifyWithBackoff = async (): Promise<SubscriptionStatusResponse> => {
  const userId = guardGetUserFromLocalStorage().id;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      const { data } =
        await SubscriptionsService.verifySubscriptionApiV1SubscriptionsVerifyPost(userId);
      return data;
    } catch (error) {
      const isLastAttempt = attempt === RETRY_DELAYS_MS.length;
      if (!isBadGateway(error) || isLastAttempt) {
        throw error;
      }
      await sleep(RETRY_DELAYS_MS[attempt]);
    }
  }

  throw new Error("[verifyWithBackoff] exhausted retries");
};
